import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { Card, Tab, Nav, Button, ButtonGroup, Row, Col } from 'react-bootstrap'
import { Tag as TagIcon, Image as ImageIcon } from 'lucide-react'
import { AnnotationDataBoundingBox } from '../../../../types/models/annotation'
import SnippetList from '../../PredictionsReview/SnippetList'
import { useAppSelector } from '../../../../store/hooks'
import { selectSelectedProjectId } from '../../../../store/slices/projectSlice'
import { useGetProjectQuery } from '../../../../services/projectsApi'
import { ProjectAnnotationType } from '../../../../types/models/project'
import ExportSnippetsModal from '../../../modals/ExportSnippetsModal'

type RightTab = 'snippets' | 'tags'

interface Props {
  contentId: string
  activeTab: RightTab
  onSelectTab: (key: RightTab) => void
  tagsContent: React.ReactNode
  boxes: AnnotationDataBoundingBox[]
  selectedBBoxClassIndex?: number
  onSnippetSelect: (box: AnnotationDataBoundingBox) => void
  hoveredBoxIndex?: number
  onHover?: (index: number | undefined) => void
  // New: Tag selection plumbing
  onToggleTagSelection?: (tagId: string, checked: boolean) => void
  selectedTagIds?: Set<string>
  // Expose selection mode toggle for header button
  onSelectionModeToggle?: (
    toggle: () => void,
    getState: () => boolean,
    setCallback: (callback: (isActive: boolean) => void) => void
  ) => void
  // Expose export action to parent (header)
  onProvideExportAction?: (exportFn: () => void, canExport: () => boolean) => void
}

const AnnotationRightPanel: React.FC<Props> = ({
  contentId,
  activeTab,
  onSelectTab,
  tagsContent,
  boxes,
  selectedBBoxClassIndex,
  onSnippetSelect,
  hoveredBoxIndex,
  onHover,
  onToggleTagSelection,
  selectedTagIds,
  onSelectionModeToggle,
  onProvideExportAction,
}) => {
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const annotationType: ProjectAnnotationType | undefined = project?.annotation_type

  const [selectionMode, setSelectionMode] = useState(false)
  // Manual snippet selections (checkboxes in snippets list)
  const [manualSelectedIndices, setManualSelectedIndices] = useState<number[]>([])
  const [showExportModal, setShowExportModal] = useState(false)
  const [rotateLongSideVertical, setRotateLongSideVertical] = useState<boolean>(() => {
    try {
      return localStorage.getItem('annotations.rotateSnippets') === '1'
    } catch {
      return false
    }
  })

  useEffect(() => {
    try {
      localStorage.setItem('annotations.rotateSnippets', rotateLongSideVertical ? '1' : '0')
    } catch {}
  }, [rotateLongSideVertical])

  // Capability-driven tabs per annotation type
  const allowedTabs = useMemo<RightTab[]>(() => {
    switch (annotationType) {
      case 'bounding_box':
      case 'oriented_bounding_box':
        return ['snippets', 'tags']
      case 'classification':
      case 'segmentation':
      default:
        return ['tags']
    }
  }, [annotationType])

  // Ensure the active tab is valid for current capabilities
  const effectiveActiveTab: RightTab = allowedTabs.includes(activeTab) ? activeTab : allowedTabs[0]

  useEffect(() => {
    if (activeTab !== effectiveActiveTab) {
      onSelectTab(effectiveActiveTab)
    }
  }, [activeTab, effectiveActiveTab, onSelectTab])

  const snippetsCount = boxes.length
  const tagSelectedIndices = React.useMemo(() => {
    if (!selectedTagIds || selectedTagIds.size === 0) return [] as number[]
    return boxes
      .map((b, i) => ({ b, i }))
      .filter(({ b }) => selectedTagIds.has(b.name))
      .map(({ i }) => i)
  }, [boxes, selectedTagIds])
  const selectedIndices = React.useMemo(
    () => Array.from(new Set<number>([...manualSelectedIndices, ...tagSelectedIndices])),
    [manualSelectedIndices, tagSelectedIndices]
  )
  const selectedCount = selectedIndices.length

  const toggleSelectIndex = (idx: number) => {
    setManualSelectedIndices((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    )
  }

  const handleSelectAll = () => {
    setManualSelectedIndices(boxes.map((_, i) => i))
  }

  const handleSelectNone = () => {
    setManualSelectedIndices([])
    // Clear tag-based selections as well to fully sync
    if (onToggleTagSelection && selectedTagIds && selectedTagIds.size > 0) {
      selectedTagIds.forEach((tagId) => onToggleTagSelection(tagId, false))
    }
  }

  const handleExport = useCallback(() => {
    if (selectedIndices.length === 0) return
    setShowExportModal(true)
  }, [selectedIndices.length])

  const stateChangeCallbackRef = React.useRef<((isActive: boolean) => void) | null>(null)

  const toggleSelectionMode = useCallback(() => {
    setSelectionMode((v) => {
      const next = !v
      // If turning selection mode off, also clear tag selections for full sync
      if (v && onToggleTagSelection && selectedTagIds && selectedTagIds.size > 0) {
        selectedTagIds.forEach((tagId) => onToggleTagSelection(tagId, false))
      }
      return next
    })
    setManualSelectedIndices([])
  }, [onToggleTagSelection, selectedTagIds])

  // Expose toggle function and state getter to parent via callback.
  // Important: only wire this up once to avoid nested update loops between
  // parent and child when `selectionMode` changes.
  React.useEffect(() => {
    if (onSelectionModeToggle) {
      onSelectionModeToggle(
        toggleSelectionMode,
        () => selectionMode,
        (callback) => {
          stateChangeCallbackRef.current = callback
        }
      )
    }
    // We intentionally do NOT depend on `selectionMode` here – the parent can
    // query the latest state via the getter or through its own callbacks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSelectionModeToggle])

  // Expose export action to parent (enabled only when selection mode active and selections exist)
  React.useEffect(() => {
    if (onProvideExportAction) {
      const canExport = () => selectionMode && selectedIndices.length > 0
      onProvideExportAction(handleExport, canExport)
    }
  }, [onProvideExportAction, selectionMode, selectedIndices.length, handleExport])

  // Tabs: 'snippets' and 'tags' only; no accept/reject actions in annotations review
  return (
    <div
      className="h-100 d-flex flex-column"
      style={{
        minHeight: 0,
        backgroundColor: '#161b22',
      }}
    >
      <div
        className="border-bottom d-flex flex-column gap-2 py-2 px-3"
        style={{
          borderColor: 'rgba(255, 255, 255, 0.1)',
          backgroundColor: '#0d1117',
        }}
      >
        {/* Selection Mode Controls */}
        {allowedTabs.includes('snippets') && selectionMode && (
          <Row className="g-2 align-items-center">
            <Col xs="auto">
              <ButtonGroup size="sm">
                <Button
                  variant="outline-light"
                  onClick={handleSelectAll}
                  className="border-secondary"
                >
                  Select All
                </Button>
                <Button
                  variant="outline-light"
                  onClick={handleSelectNone}
                  className="border-secondary"
                >
                  Clear
                </Button>
              </ButtonGroup>
            </Col>
            <Col className="text-end">
              <span className="text-light small fw-semibold">
                {selectedCount} {selectedCount === 1 ? 'item' : 'items'} selected
              </span>
            </Col>
          </Row>
        )}

        {/* Rotate Snippets Toggle */}
        {allowedTabs.includes('snippets') && annotationType === 'oriented_bounding_box' && (
          <Row>
            <Col>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="rotateSnippetsSwitch"
                  checked={rotateLongSideVertical}
                  onChange={(e) => setRotateLongSideVertical(e.target.checked)}
                />
                <label className="form-check-label text-light small" htmlFor="rotateSnippetsSwitch">
                  Rotate snippets (align long side vertical)
                </label>
              </div>
            </Col>
          </Row>
        )}

        {/* Tab Navigation - Compact Labelbox Style */}
        <Tab.Container
          activeKey={effectiveActiveTab}
          onSelect={(k) => onSelectTab(k === 'snippets' ? 'snippets' : 'tags')}
        >
          <Nav variant="tabs" className="w-100 border-0">
            {allowedTabs.includes('snippets') && (
              <Nav.Item>
                <Nav.Link
                  eventKey="snippets"
                  className={`px-3 py-2 border-0 ${
                    effectiveActiveTab === 'snippets' ? 'active' : ''
                  }`}
                  style={
                    effectiveActiveTab === 'snippets'
                      ? {
                          backgroundColor: '#161b22',
                          color: '#58a6ff',
                          borderBottom: '2px solid #58a6ff',
                        }
                      : {
                          color: '#8b949e',
                          backgroundColor: 'transparent',
                        }
                  }
                >
                  <div className="d-flex align-items-center gap-2">
                    <ImageIcon size={16} />
                    <span className="small">Objects</span>
                    <span className="badge bg-secondary">{snippetsCount}</span>
                  </div>
                </Nav.Link>
              </Nav.Item>
            )}
            <Nav.Item>
              <Nav.Link
                eventKey="tags"
                className={`px-3 py-2 border-0 ${effectiveActiveTab === 'tags' ? 'active' : ''}`}
                style={
                  effectiveActiveTab === 'tags'
                    ? {
                        backgroundColor: '#161b22',
                        color: '#58a6ff',
                        borderBottom: '2px solid #58a6ff',
                      }
                    : {
                        color: '#8b949e',
                        backgroundColor: 'transparent',
                      }
                }
              >
                <div className="d-flex align-items-center gap-2">
                  <TagIcon size={16} />
                  <span className="small">Tags</span>
                </div>
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </Tab.Container>
      </div>

      <div
        className="flex-grow-1 d-flex flex-column overflow-hidden"
        style={{ minHeight: 0, backgroundColor: '#161b22' }}
      >
        <Tab.Container
          activeKey={effectiveActiveTab}
          onSelect={(k) => onSelectTab(k === 'snippets' ? 'snippets' : 'tags')}
        >
          <Tab.Content className="flex-grow-1 overflow-auto h-100" style={{ minHeight: 0 }}>
            <Tab.Pane eventKey="tags" className="h-100 px-2 py-2">
              {React.isValidElement(tagsContent)
                ? React.cloneElement(
                    tagsContent as React.ReactElement<{
                      onToggleTagSelection?: (tagId: string, checked: boolean) => void
                      selectedTagIds?: Set<string>
                      selectionMode?: boolean
                    }>,
                    {
                      onToggleTagSelection,
                      selectedTagIds,
                      selectionMode,
                    }
                  )
                : tagsContent}
            </Tab.Pane>
            {allowedTabs.includes('snippets') && (
              <Tab.Pane eventKey="snippets" className="h-100 px-2 py-2">
                <SnippetList
                  contentId={contentId}
                  items={boxes}
                  selectedBBoxClassIndex={selectedBBoxClassIndex}
                  showConfidence={false}
                  showAcceptButton={false}
                  showRejectButton={false}
                  hideFilters={true}
                  hoveredBoxIndex={hoveredBoxIndex}
                  onHover={onHover}
                  onSelect={(box) => onSnippetSelect(box)}
                  selectionMode={selectionMode}
                  selectedIndices={selectedIndices}
                  onToggleSelect={toggleSelectIndex}
                  rotateLongSideVertical={
                    annotationType === 'oriented_bounding_box' && rotateLongSideVertical
                  }
                />
              </Tab.Pane>
            )}
          </Tab.Content>
        </Tab.Container>
        {showExportModal && (
          <ExportSnippetsModal
            show={showExportModal}
            onHide={() => setShowExportModal(false)}
            contentId={contentId}
            boxes={selectedIndices.map((i) => boxes[i])}
          />
        )}
      </div>
    </div>
  )
}

export default AnnotationRightPanel
